import React, { useState } from 'react';
import { LayoutDashboard, CreditCard, Banknote, LogOut, ShieldCheck, Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import logo from '../logo.png';

const navItems = [
  { name: 'Dashboard', icon: LayoutDashboard },
  { name: 'Payouts', icon: Banknote },
  { name: 'History', icon: CreditCard },
  { name: 'Admins', icon: ShieldCheck },
];

interface NavbarProps {
  currentPage?: string;
  setCurrentPage?: (page: string) => void;
}

const Navbar: React.FC<NavbarProps> = ({ currentPage = 'Dashboard', setCurrentPage = () => {} }) => {
  const { user, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  return (
    <nav className="bg-white border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center">
        {/* Left: Logo */}
        <div className="flex-1 flex justify-start">
          <div className="text-lg sm:text-xl font-extrabold tracking-tighter text-[#121415] flex items-center gap-2 cursor-pointer" onClick={() => setCurrentPage('Dashboard')}>
            <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-[10px] overflow-hidden shadow-lg shadow-[#05A664]/15 flex items-center justify-center border border-black/5">
              <img src={logo} alt="Travel Pass" className="w-full h-full object-cover" />
            </div>
            <span>TRAVEL PASS <span className="text-gray-400 font-medium uppercase text-[9px] sm:text-[10px] tracking-[0.2em] ml-1">Admin</span></span>
          </div>
        </div>

        {/* Center: Nav Items (Desktop) */}
        <div className="hidden md:flex items-center gap-1 px-4 py-1.5 bg-gray-50 rounded-[1.5rem] border border-gray-100">
          {navItems.map((item) => (
            <button
              key={item.name}
              onClick={() => setCurrentPage(item.name)}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-2xl text-sm font-bold transition-all ${currentPage === item.name
                  ? 'bg-[#121415] text-white shadow-xl shadow-black/10'
                  : 'text-[#909090] hover:bg-white hover:text-[#05A664]'
                }`}
            >
              <item.icon size={18} />
              {item.name}
            </button>
          ))}
        </div>

        {/* Right: User Actions */}
        <div className="flex-1 flex justify-end items-center gap-2.5 sm:gap-4">
          <button 
            onClick={logout}
            className="p-2 sm:p-2.5 text-gray-400 hover:text-red-600 transition-colors bg-gray-50 rounded-xl"
            title="Logout"
          >
            <LogOut size={18} />
          </button>
          <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-xl bg-gradient-to-tr from-[#05A664] to-[#04D982] shadow-lg shadow-[#05A664]/20 flex items-center justify-center font-black text-white text-xs border border-white/20">
            {user?.name ? getUserInitials(user.name) : 'AD'}
          </div>

          {/* Mobile Menu Toggle Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 sm:p-2.5 text-gray-600 hover:text-[#121415] bg-gray-50 rounded-xl hover:bg-gray-100 transition-all border border-gray-100 flex items-center justify-center"
          >
            {isMobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Expandable Mobile Navigation Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="md:hidden overflow-hidden border-t border-gray-50 bg-white"
          >
            <div className="px-4 sm:px-8 py-4 flex flex-col gap-2">
              {navItems.map((item) => (
                <button
                  key={item.name}
                  onClick={() => {
                    setCurrentPage(item.name);
                    setIsMobileMenuOpen(false);
                  }}
                  className={`flex items-center gap-3 w-full px-5 py-3.5 rounded-2xl text-sm font-bold transition-all ${
                    currentPage === item.name
                      ? 'bg-[#121415] text-white shadow-lg shadow-black/10'
                      : 'text-[#909090] bg-gray-50/50 hover:bg-gray-50 hover:text-[#05A664]'
                  }`}
                >
                  <item.icon size={18} />
                  {item.name}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
